import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Printer, ChefHat, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Kitchen ticket — printable, no prices, big readable item list,
 * modifiers and special notes in bold for line cooks.
 *
 * Opened from the dashboard's order card (Kitchen button) → new tab → auto-print.
 */
const OrderKitchenTicket = () => {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/orders/${orderId}/print`)
      .then((r) => {
        if (!mounted) return;
        setData(r.data);
        const auto = new URLSearchParams(window.location.search).get('auto');
        if (auto !== '0') setTimeout(() => window.print(), 700);
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Order not found'));
    return () => { mounted = false; };
  }, [orderId]);

  if (err) return <CenterMessage title="Ticket unavailable" sub={err} />;
  if (!data) return <CenterMessage title="Preparing kitchen ticket…" loading />;

  const { order } = data;
  const created = new Date(order.created_at || Date.now());
  const orderTypeLabel = {
    delivery: 'DELIVERY', pickup: 'PICKUP',
    dine_in: 'DINE-IN', counter: 'COUNTER',
  }[order.order_type] || (order.order_type || 'ORDER').toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="no-print sticky top-0 z-10 bg-black text-white border-b border-emerald-500/40 px-4 py-3 flex items-center justify-between">
        <div className="text-sm inline-flex items-center gap-2">
          <ChefHat className="w-4 h-4 text-emerald-400" />
          <span className="text-emerald-400 font-bold">Kitchen Ticket</span>
          <span className="text-white/55 ml-2">#{order.order_number || order.id?.slice(-8)}</span>
        </div>
        <button
          onClick={() => window.print()}
          data-testid="print-kitchen-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      <div
        className="mx-auto my-6 print:my-0 bg-white text-black w-[80mm] print:w-full max-w-[80mm] print:max-w-none p-5 print:p-3 font-mono leading-[1.4] shadow-sm print:shadow-none"
        data-testid="kitchen-ticket-page"
      >
        {/* Big banner */}
        <div className="text-center pb-2 mb-2 border-b-2 border-black">
          <div className="text-[18px] font-black tracking-tight">{orderTypeLabel}</div>
          <div className="text-[24px] font-black tabular-nums leading-none mt-1">
            #{order.order_number || (order.id || '').slice(-6)}
          </div>
          {order.table_number ? (
            <div className="mt-1 text-[14px] font-bold">TABLE {order.table_number}</div>
          ) : null}
          <div className="text-[10.5px] mt-1.5">{created.toLocaleString()}</div>
        </div>

        {/* Customer brief (no phone — kitchen doesn't need it) */}
        {order.customer_name ? (
          <div className="mb-2 pb-2 border-b border-dashed border-black/40 text-[11.5px]">
            <span className="text-black/60">Customer:</span> <span className="font-bold">{order.customer_name}</span>
          </div>
        ) : null}

        {/* Items — BIG fonts, no prices */}
        <div className="space-y-3">
          {(order.items || []).map((it, idx) => (
            <div key={idx} className="border-b border-dashed border-black/30 pb-2 last:border-b-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[20px] font-black leading-none tabular-nums">{it.quantity}×</span>
                <span className="text-[15px] font-bold uppercase">{it.name}</span>
              </div>
              {(it.modifiers || []).length > 0 && (
                <ul className="mt-1.5 ml-5 list-disc list-outside text-[13px] font-bold">
                  {it.modifiers.map((m, i) => (
                    <li key={i}>
                      {m.name}
                      {(m.quantity || 1) > 1 ? ` ×${m.quantity}` : ''}
                    </li>
                  ))}
                </ul>
              )}
              {it.notes ? (
                <div className="mt-1.5 ml-1 p-1.5 border-2 border-black text-[12.5px] font-bold uppercase">
                  ⚠ {it.notes}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Special order-level instructions */}
        {order.notes ? (
          <div className="mt-3 p-2 border-2 border-black text-[12px] font-bold uppercase">
            ★ ORDER NOTE: {order.notes}
          </div>
        ) : null}

        {/* Footer */}
        <div className="mt-3 pt-2 border-t-2 border-black text-center text-[10px]">
          NoFee.restaurant — Kitchen Copy
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { size: 80mm auto; margin: 4mm; }
        }
      `}</style>
    </div>
  );
};

const CenterMessage = ({ title, sub, loading }) => (
  <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-slate-50">
    {loading ? <Loader2 className="w-7 h-7 text-emerald-500 animate-spin mb-3" /> : null}
    <div className="text-lg font-bold text-slate-900">{title}</div>
    {sub ? <div className="mt-1 text-sm text-slate-500">{sub}</div> : null}
  </div>
);

export default OrderKitchenTicket;
